import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCompanyDashboard, type DashboardResponse } from "../../services/dashboard-api";
import { DashboardCharts } from "../../components/dashboard/DashboardCharts";
import { ResultsTable } from "../../components/dashboard/ResultsTable";
import { Ranking } from "../../components/dashboard/Ranking";
import { DepartmentFilter } from "../../components/dashboard/DepartmentFilter";

/** FR-042: a CO Member can view any company's dashboard — the identical charts/table/ranking a
 * Manager sees for their own company, assembled from the same shared components. */
export function CompanyDashboardPage() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [allDepartments, setAllDepartments] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);

  useEffect(() => {
    if (!companyId) return;
    getCompanyDashboard(companyId, []).then((d) => {
      setAllDepartments(
        Array.from(new Set(d.table.map((r) => r.department).filter((d): d is string => !!d))),
      );
    });
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    getCompanyDashboard(companyId, selected).then(setDashboard);
  }, [companyId, selected]);

  if (!dashboard) return <div className="p-6 text-body">Loading…</div>;

  return (
    <div className="p-6">
      <button onClick={() => navigate("/co-member/dashboard")} className="mb-4 text-sm text-brand">
        ← Back to companies
      </button>
      <h1 className="mb-4 font-sans text-xl text-ink">{t("dashboard.title", "Report")}</h1>
      <DepartmentFilter options={allDepartments} selected={selected} onChange={setSelected} />

      <div className="mb-8">
        <DashboardCharts charts={dashboard.charts} />
      </div>

      {/* FR-075: ranking sits above the results table. */}
      <div className="mb-8">
        <h2 className="mb-2 font-sans text-lg text-ink">{t("dashboard.ranking", "Ranking")}</h2>
        <Ranking entries={dashboard.ranking} />
      </div>

      <div>
        <h2 className="mb-2 font-sans text-lg text-ink">{t("dashboard.results", "Results")}</h2>
        <ResultsTable rows={dashboard.table} />
      </div>
    </div>
  );
}
